import { CustomElementBase } from 'app/shared/elements/custom-element-base';
import { EssDropdownCustomElement } from 'app/shared/elements/ess-dropdown';
import { CompassFlags } from 'app/shared/flags/compass-flags';
import { getComponentViewModel } from 'app/shared/lib/aurelia';
import { guid } from 'app/shared/lib/guid';
import { bindable, processContent } from 'aurelia-framework';

interface IEssButtonType {
    key: string;
    icon: string;
}

function asKey(type: string) {
    return `ui:actions.${type}`;
}
function asIcon(type: string) {
    return `ui:icons.${type}`;
}

const TYPES = new Map<string, IEssButtonType>([
    // ['add', { key: asKey('add'), icon: asIcon('add') }]
]);

@processContent(EssButtonCustomElement.processContent)
export class EssButtonCustomElement extends CustomElementBase {

    @bindable() protected type: string | null = null;
    @bindable() protected icon: string | null = null;
    @bindable() protected disabled: boolean = false;
    @bindable() protected what: string | null = null;
    @bindable() protected testName: string | null = null;
    @bindable() protected tooltip: string | null = null;
    @bindable() protected tooltipPosition: string | null = 'bottom';
    @bindable() protected autofocus: boolean = false;
    @bindable() protected controlsId: string | null = null;
    @bindable() protected describedbyId: string | null = null;  //An element's `id`. Sets the button's `aria-describedby`
    @bindable() protected arialabelValue: string | null = null;


    protected id = guid();
    protected typeIcon: string | null = null;
    protected key: string | null = null;

    protected observer: MutationObserver = new MutationObserver(this.handleMutation);
    protected dropdown: EssDropdownCustomElement | null = null;

    protected handleClick = (event: MouseEvent) => {
        if(this.disabled) {
            event.preventDefault();
            event.stopPropagation();
            return
        }

        if(this.dropdown !== null) {
            // if the target is inside the dropdown, close
            if(this.dropdown.element.contains(event.target as Element)) {
                this.dropdown.close();
            }
            // if the target is the button, open
            else {
                let direction: CompassFlags = 0;
                const { top, height, left, width } = this.element.getBoundingClientRect();
                const { clientHeight, clientWidth } = document.body;
                direction |= top + height / 2 >= clientHeight / 2 ? CompassFlags.Up : CompassFlags.Down;
                direction |= left + width / 2 >= clientWidth / 2 ? CompassFlags.Left : CompassFlags.Right;
                this.dropdown.toggle(direction);
            }
            event.preventDefault();
        }
    };

    protected attached() {
        this.observer.observe(this.element, { childList: true });
        this.handleMutation();
        this.element.addEventListener('click', this.handleClick as EventListener);
    }

    protected detached() {
        this.observer.disconnect();
        this.element.removeEventListener('click', this.handleClick as EventListener);
    }

    protected typeChanged(type: string | null) {
        if(type === null) {
            this.key = this.typeIcon = type;
        } else if(TYPES.has(type)) {
            const { key, icon } = TYPES.get(type)!;
            if(key) {
                this.key = key;
            }
            if(icon) {
                this.typeIcon = icon;
            }
        } else {
            this.key = asKey(type);
            this.typeIcon = asIcon(type);
        }
    }

    protected handleMutation() {
        // TODO use tabled class
        const dropdown = this.element.querySelector('.ess-dropdown');
        if(dropdown) {
            this.dropdown = getComponentViewModel(dropdown) as EssDropdownCustomElement;
            this.dropdown.element.setAttribute('aria-labelledby', `${this.id}-text`);
        } else {
            this.dropdown = null;
        }
    }

    static processContent(_a: any, _b: any, node: Element, _d: any): true {
        const dropdown = node.querySelector('ess-dropdown');
        if(dropdown) {
            node.classList.add('ess-dropdown-button');
            dropdown.setAttribute('slot', 'dropdown');
        }
        return true;
    }
}
